import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/** Convert a #rrggbb / #rgb hex colour into the "H S% L%" string Tailwind tokens expect. */
export function hexToHslTokens(hex: string): string | null {
  const value = hex.trim().replace(/^#/, "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const setMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/**
 * Applies the CMS-managed colour scheme and site-wide SEO metadata at runtime.
 * Colours are written as CSS variables so every semantic token follows them.
 */
export function SiteBranding() {
  const { settings } = useSiteSettings();

  useEffect(() => {
    const root = document.documentElement;
    const apply = (vars: string[], hex: string | null) => {
      if (!hex) return;
      const hsl = hexToHslTokens(hex);
      if (!hsl) return;
      vars.forEach((v) => root.style.setProperty(v, hsl));
    };

    apply(["--primary", "--ring", "--gold"], settings.brand.primary);
    apply(["--accent"], settings.brand.accent);
    apply(["--background", "--card", "--popover"], settings.brand.background);
    apply(["--foreground", "--card-foreground", "--popover-foreground"], settings.brand.foreground);
  }, [settings.brand.primary, settings.brand.accent, settings.brand.background, settings.brand.foreground]);

  // Title/description/canonical are handled per-route by <RouteMeta />.
  useEffect(() => {
    if (settings.ogImageUrl) {
      setMeta('meta[property="og:image"]', "property", "og:image", settings.ogImageUrl);
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", settings.ogImageUrl);
      setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    }
  }, [settings.ogImageUrl]);


  return null;
}
